import PageHeader from '../components/ui/PageHeader'
import ChildCareIcon from '@offline-mui/icons-material/ChildCare'
import CloseIcon from '@offline-mui/icons-material/Close'
import FlightLandIcon from '@offline-mui/icons-material/FlightLand'
import FlightTakeoffIcon from '@offline-mui/icons-material/FlightTakeoff'
import PersonIcon from '@offline-mui/icons-material/Person'
import SaveIcon from '@offline-mui/icons-material/Save'
import ScheduleIcon from '@offline-mui/icons-material/Schedule'
import StrollerIcon from '@offline-mui/icons-material/Stroller'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@offline-mui/material'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from '../router'
import { createInventory, fetchInventoryById, fetchSabreFlightSchedules, updateBlockActive, updateInventory } from '../api/inventory'
import type { BaggageAllowance, CreateInventoryPayload, InventoryFare, InventoryItinerary, SabreScheduleOption } from '../api/inventory'
import {
  toDateInput,
  toDateTimeLocalInput,
  parseMoney,
} from '../utils/flightFormat'
import BlockDetailsSection, { EMPTY_DISTRIBUTION, bookedExceedsBlocked, distributionToPayload } from '../components/BlockDetailsSection'
import type { BlockDetailsValues, DistributionDraft } from '../components/BlockDetailsSection'
import SabreScheduleOptionCard from '../components/SabreScheduleOptionCard'
import SelectedFlightsCard from '../components/SelectedFlightsCard'
import { DateField } from '../components/ui/DateField'

const CABINS = ['Economy', 'Premium Economy', 'Business']
/** Fare type is no longer chosen in the UI; every block is saved as Negotiated. */
const DEFAULT_FARE_TYPE = 'Negotiated'

const CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP']

type TripType = 'OW' | 'RT'

type FareBasisDraft = {
  cabin: string
  classOfService: string
  fareBasisCode: string
  brandCode: string
  brandName: string
}

type FlightDraft = {
  key: string
  carrier: string
  flightNumber: string
  operatingCarrier: string
  operatingCarrierName: string
  origin: string
  destination: string
  departureDatetime: string
  arrivalDatetime: string
  aircraftType: string
  durationMin: string
  fareBasis: FareBasisDraft
}

type SegmentDraft = {
  key: string
  label: string
  origin: string
  destination: string
  departureDate: string
  cabin: string
  brandCode: string
  brandName: string
  flights: FlightDraft[]
  scheduleOptions: SabreScheduleOption[]
  selectedOptionIndex: number | null
  loadingSchedules: boolean
  scheduleError: string | null
  editingSchedule: boolean
  /** Search fields as they were before "Change schedule", restored on cancel. */
  scheduleBackup: {
    origin: string
    destination: string
    departureDate: string
    cabin: string
  } | null
}

const EMPTY_FARE_BASIS: FareBasisDraft = {
  cabin: '',
  classOfService: '',
  fareBasisCode: '',
  brandCode: '',
  brandName: '',
}

type PaxCode = 'ADT' | 'CHD' | 'INF'

type PricingDraft = {
  baseAmount: string
  totalTaxes: string
  packageOnlyMarkup: string
  airOnlyMarkup: string
}

const DEFAULT_CURRENCY = 'CAD'

const EMPTY_PRICING: PricingDraft = {
  baseAmount: '',
  totalTaxes: '',
  packageOnlyMarkup: '',
  airOnlyMarkup: '',
}

const PAX_TYPES: Array<{
  code: PaxCode
  type: string
  label: string
  icon: typeof PersonIcon
}> = [
  { code: 'ADT', type: 'Adult', label: 'Adult', icon: PersonIcon },
  { code: 'CHD', type: 'Child', label: 'Child', icon: ChildCareIcon },
  { code: 'INF', type: 'InfantInLap', label: 'Infant', icon: StrollerIcon },
]

const EMPTY_PAX_PRICING: Record<PaxCode, PricingDraft> = {
  ADT: { ...EMPTY_PRICING },
  CHD: { ...EMPTY_PRICING },
  INF: { ...EMPTY_PRICING },
}

function isPaxFilled(pricing: PricingDraft) {
  return (
    pricing.baseAmount !== '' &&
    pricing.totalTaxes !== '' &&
    Number.isFinite(Number(pricing.baseAmount)) &&
    Number.isFinite(Number(pricing.totalTaxes))
  )
}

function isPaxEmpty(pricing: PricingDraft) {
  return pricing.baseAmount === '' && pricing.totalTaxes === ''
}

function paxTotal(pricing: PricingDraft) {
  const base = Number(pricing.baseAmount)
  const taxes = Number(pricing.totalTaxes)
  if (pricing.baseAmount === '' && pricing.totalTaxes === '') return ''
  if (!Number.isFinite(base) || !Number.isFinite(taxes)) return ''
  // Markups are tracked separately and are never included in total.
  return (base + taxes).toFixed(2)
}

function fareToPricingDraft(fare: InventoryFare | undefined): PricingDraft {
  if (!fare) return { ...EMPTY_PRICING }
  const base = parseMoney(fare.base_amount)
  const taxes = parseMoney(fare.total_taxes)
  const packageMarkup = parseMoney(fare.package_only_markup)
  const airMarkup = parseMoney(fare.air_only_markup)
  return {
    baseAmount: base != null ? String(base.amount) : '',
    totalTaxes: taxes != null ? String(taxes.amount) : '',
    packageOnlyMarkup:
      packageMarkup != null ? String(packageMarkup.amount) : '',
    airOnlyMarkup: airMarkup != null ? String(airMarkup.amount) : '',
  }
}

/** One currency applies to every passenger type; read it off the first fare. */
function currencyFromFares(fares: InventoryFare[] | null | undefined): string {
  for (const fare of fares ?? []) {
    const money = parseMoney(fare.base_amount) ?? parseMoney(fare.total_taxes)
    if (money?.currency) return money.currency.toUpperCase()
  }
  return DEFAULT_CURRENCY
}

function faresToPaxPricing(fares: InventoryFare[] | null | undefined) {
  const next = {
    ADT: { ...EMPTY_PRICING },
    CHD: { ...EMPTY_PRICING },
    INF: { ...EMPTY_PRICING },
  }
  ;(fares ?? []).forEach((fare) => {
    const code = String(fare.passenger_code || '').toUpperCase()
    const type = String(fare.passenger_type || '').toUpperCase()
    const key: PaxCode =
      code === 'CHD' || type === 'CHILD'
        ? 'CHD'
        : code === 'INF' || type === 'INFANT' || type === 'INFANTINLAP'
          ? 'INF'
          : 'ADT'
    next[key] = fareToPricingDraft(fare)
  })
  return next
}

function isFareBasisReady(fareBasis: FareBasisDraft) {
  return Boolean(fareBasis.cabin)
}

function toFareBasisDraft(
  basis:
    | {
        cabin?: string | null
        class_of_service?: string | null
        fare_basis_code?: string | null
        brand_code?: string | null
        brand_name?: string | null
      }
    | null
    | undefined,
): FareBasisDraft {
  if (!basis) return { ...EMPTY_FARE_BASIS }
  return {
    cabin: basis.cabin || '',
    classOfService: basis.class_of_service || '',
    fareBasisCode: basis.fare_basis_code || '',
    brandCode: basis.brand_code || '',
    brandName: basis.brand_name || '',
  }
}

function isFlightReady(flight: FlightDraft) {
  return Boolean(
    flight.carrier &&
      flight.flightNumber &&
      flight.origin &&
      flight.destination &&
      flight.departureDatetime &&
      flight.arrivalDatetime &&
      flight.durationMin &&
      Number(flight.durationMin) > 0 &&
      isFareBasisReady(flight.fareBasis),
  )
}

/** Parse Sabre "HH:MM" duration strings into total minutes. */
function parseDurationToMinutes(duration: string | undefined | null): string {
  if (!duration) return ''
  const match = String(duration).trim().match(/^(\d{1,3}):(\d{2})$/)
  if (!match) return ''
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return ''
  return String(hours * 60 + minutes)
}

function newFlight(partial?: Partial<FlightDraft>): FlightDraft {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    carrier: '',
    flightNumber: '',
    operatingCarrier: '',
    operatingCarrierName: '',
    origin: '',
    destination: '',
    departureDatetime: '',
    arrivalDatetime: '',
    aircraftType: '',
    durationMin: '',
    fareBasis: { ...EMPTY_FARE_BASIS },
    ...partial,
  }
}

function segmentFareBasis(segment: Pick<
  SegmentDraft,
  'cabin' | 'brandCode' | 'brandName'
>): FareBasisDraft {
  return {
    cabin: segment.cabin,
    classOfService: '',
    fareBasisCode: '',
    brandCode: segment.brandCode,
    brandName: segment.brandName,
  }
}

function sabreOptionToFlights(
  option: SabreScheduleOption,
  fareBasis: FareBasisDraft,
): FlightDraft[] {
  return (option.segments ?? []).map((leg) =>
    newFlight({
      carrier: leg.marketingCarrier || '',
      flightNumber: leg.flightNumber || '',
      operatingCarrier: leg.operatingCarrier || '',
      origin: leg.origin || '',
      destination: leg.destination || '',
      departureDatetime: toDateTimeLocalInput(leg.departureDateTime),
      arrivalDatetime: toDateTimeLocalInput(leg.arrivalDateTime),
      aircraftType: leg.equipmentType || '',
      durationMin: parseDurationToMinutes(leg.duration),
      fareBasis: { ...fareBasis },
    }),
  )
}

function emptySegmentFields(): Omit<SegmentDraft, 'key' | 'label'> {
  return {
    origin: '',
    destination: '',
    departureDate: '',
    cabin: '',
    brandCode: '',
    brandName: '',
    flights: [],
    scheduleOptions: [],
    selectedOptionIndex: null,
    loadingSchedules: false,
    scheduleError: null,
    editingSchedule: false,
    scheduleBackup: null,
  }
}

function buildSegments(tripType: TripType): SegmentDraft[] {
  if (tripType === 'RT') {
    return [
      { key: 'seg-1', label: 'Outbound', ...emptySegmentFields() },
      { key: 'seg-2', label: 'Inbound', ...emptySegmentFields() },
    ]
  }
  return [{ key: 'seg-1', label: 'Outbound', ...emptySegmentFields() }]
}

/** YYYY-MM-DD + 1 calendar day (local). */
function nextCalendarDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  if (!year || !month || !day) return isoDate
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + 1)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function isReturnDepartureAfterOutbound(
  outboundDate: string,
  returnDate: string,
): boolean {
  return Boolean(outboundDate && returnDate && returnDate > outboundDate)
}

function isItineraryComplete(
  platingCarrier: string,
  fareType: string,
  ticketingDate: string,
) {
  return Boolean(platingCarrier && fareType && ticketingDate)
}

function pnrsToFormValue(value: string | null | undefined): string {
  if (!value) return ''
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.map(String).join(', ')
  } catch {
    // already plain text
  }
  return value
}

function agenciesToFormValue(value: string | string[] | null | undefined): string {
  if (!value) return ''
  if (Array.isArray(value)) return value.map(String).join(', ')
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.map(String).join(', ')
  } catch {
    // already plain text
  }
  return value
}

function distributionFromRow(row: InventoryItinerary): DistributionDraft {
  const summary = row.summary
  const legacySource = summary?.source ?? row.source ?? null
  const legacyInclude = summary?.agency_include ?? row.agency_include
  const legacyExclude = summary?.agency_exclude ?? row.agency_exclude
  const gigiEnabled = summary?.distribute_gigi ?? row.distribute_gigi ?? legacySource === 'GIGI'
  const apiEnabled = summary?.distribute_api ?? row.distribute_api ?? legacySource === 'API'
  return {
    gigi: {
      enabled: Boolean(gigiEnabled),
      include: agenciesToFormValue(
        summary?.gigi_agency_include ??
          row.gigi_agency_include ??
          (legacySource === 'GIGI' ? legacyInclude : null),
      ),
      exclude: agenciesToFormValue(
        summary?.gigi_agency_exclude ??
          row.gigi_agency_exclude ??
          (legacySource === 'GIGI' ? legacyExclude : null),
      ),
    },
    api: {
      enabled: Boolean(apiEnabled),
      include: agenciesToFormValue(
        summary?.api_agency_include ??
          row.api_agency_include ??
          (legacySource === 'API' ? legacyInclude : null),
      ),
      exclude: agenciesToFormValue(
        summary?.api_agency_exclude ??
          row.api_agency_exclude ??
          (legacySource === 'API' ? legacyExclude : null),
      ),
    },
  }
}

function bagsToFormValue(value: BaggageAllowance | null | undefined): string {
  return value?.quantity != null ? String(value.quantity) : ''
}

function bagsToPayload(value: string): BaggageAllowance | null {
  if (value.trim() === '') return null
  const quantity = Number(value)
  return Number.isInteger(quantity) && quantity >= 0 ? { quantity } : null
}

/** Seat counts are always sent; a blank field means zero. */
function seatCount(value: string): number {
  const num = Number(value)
  return value.trim() !== '' && Number.isFinite(num) && num >= 0 ? Math.floor(num) : 0
}

function emptyFormState() {
  return {
    tripType: '' as TripType | '',
    platingCarrier: '',
    fareType: DEFAULT_FARE_TYPE,
    ticketingDate: '',
    totalSeatsBlocked: '0',
    totalSeatsBooked: '0',
    multiplePnrs: '',
    blockSource: '',
    distribution: EMPTY_DISTRIBUTION,
    freeCabinBags: '',
    freeCheckedBags: '',
    seatReleaseDate: '',
    fareRulesEn: '',
    fareRulesFr: '',
    segments: [] as SegmentDraft[],
    pricing: EMPTY_PAX_PRICING,
    currency: DEFAULT_CURRENCY,
  }
}

function itineraryToSegments(row: InventoryItinerary): SegmentDraft[] {
  const segments = [...(row.segments ?? [])].sort(
    (a, b) => a.segment_order - b.segment_order,
  )
  return segments.map((segment, index) => {
    const flights = [...(segment.flights ?? [])].sort(
      (a, b) => a.flight_order - b.flight_order,
    )
    const mappedFlights = flights.map((flight) =>
      newFlight({
        carrier: flight.carrier || '',
        flightNumber: flight.flight_number || '',
        operatingCarrier: flight.operating_carrier || '',
        operatingCarrierName: flight.operating_carrier_name || '',
        origin: flight.departure_airport,
        destination: flight.arrival_airport,
        departureDatetime: toDateTimeLocalInput(flight.departure_datetime),
        arrivalDatetime: toDateTimeLocalInput(flight.arrival_datetime),
        aircraftType: flight.aircraft_type || '',
        durationMin: String(flight.duration_min || ''),
        fareBasis: toFareBasisDraft(
          flight.fare_basis ??
            row.fare_basis?.find((item) => item.flight_id === flight.flight_id),
        ),
      }),
    )
    const firstFlight = flights[0]
    const lastFlight = flights[flights.length - 1]
    const firstFare = mappedFlights[0]?.fareBasis ?? EMPTY_FARE_BASIS
    return {
      key: `seg-${segment.segment_id || index + 1}`,
      label: index === 0 ? 'Outbound' : 'Inbound',
      origin: firstFlight?.departure_airport || '',
      destination: lastFlight?.arrival_airport || '',
      departureDate: toDateInput(firstFlight?.departure_datetime),
      cabin: firstFare.cabin,
      brandCode: firstFare.brandCode,
      brandName: firstFare.brandName,
      flights: mappedFlights,
      scheduleOptions: [],
      selectedOptionIndex: null,
      loadingSchedules: false,
      scheduleError: null,
      editingSchedule: false,
      scheduleBackup: null,
    }
  })
}

export default function CreateInventoryPage() {
  const [searchParams] = useSearchParams()
  const editingId = Number(searchParams.get('id')) || null
  const loadedEditRef = useRef<number | null>(null)

  const [tripType, setTripType] = useState<TripType | ''>('')
  const [platingCarrier, setPlatingCarrier] = useState('')
  const [fareType, setFareType] = useState(DEFAULT_FARE_TYPE)
  const [ticketingDate, setTicketingDate] = useState('')
  const [totalSeatsBlocked, setTotalSeatsBlocked] = useState('0')
  const [totalSeatsBooked, setTotalSeatsBooked] = useState('0')
  const [multiplePnrs, setMultiplePnrs] = useState('')
  const [blockSource, setBlockSource] = useState('')
  const [distribution, setDistribution] = useState<DistributionDraft>(EMPTY_DISTRIBUTION)
  const [freeCabinBags, setFreeCabinBags] = useState('')
  const [freeCheckedBags, setFreeCheckedBags] = useState('')
  const [seatReleaseDate, setSeatReleaseDate] = useState('')
  const [fareRulesEn, setFareRulesEn] = useState('')
  const [fareRulesFr, setFareRulesFr] = useState('')
  const [segments, setSegments] = useState<SegmentDraft[]>([])
  const [pricing, setPricing] = useState(EMPTY_PAX_PRICING)
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY)
  const [activeTab, setActiveTab] = useState(0)
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editingId))
  const [isActive, setIsActive] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const hasCompletePaxFare = PAX_TYPES.some((pax) => isPaxFilled(pricing[pax.code]))
  const hasPartialPaxFare = PAX_TYPES.some(
    (pax) => !isPaxEmpty(pricing[pax.code]) && !isPaxFilled(pricing[pax.code]),
  )

  const returnDatesValid =
    tripType !== 'RT' ||
    segments.length < 2 ||
    isReturnDepartureAfterOutbound(
      segments[0]?.departureDate || '',
      segments[1]?.departureDate || '',
    )

  const seatsOverbooked = bookedExceedsBlocked(totalSeatsBlocked, totalSeatsBooked)

  const canSubmit = Boolean(
    tripType &&
      !seatsOverbooked &&
      isItineraryComplete(platingCarrier, fareType, ticketingDate) &&
      hasCompletePaxFare &&
      !hasPartialPaxFare &&
      segments.length > 0 &&
      returnDatesValid &&
      segments.every(
        (segment) =>
          Boolean(segment.cabin) &&
          segment.flights.length > 0 &&
          segment.flights.every(isFlightReady),
      ),
  )

  useEffect(() => {
    if (!editingId) {
      loadedEditRef.current = null
      setLoadingEdit(false)
      return
    }
    if (loadedEditRef.current === editingId) return
    loadedEditRef.current = editingId

    const load = async () => {
      setLoadingEdit(true)
      setError(null)
      try {
        const response = await fetchInventoryById(editingId)
        const row = response.data
        const nextTripType: TripType =
          (row.segments?.length || 0) > 1 ? 'RT' : 'OW'
        setTripType(nextTripType)
        setPlatingCarrier(row.validating_carrier || row.plating_carrier || '')
        setFareType(row.fare_type || DEFAULT_FARE_TYPE)
        setTicketingDate(
          toDateInput(row.summary?.last_ticketable_date ?? row.last_ticketable_date),
        )
        setTotalSeatsBlocked(
          String(row.summary?.total_seats_blocked ?? row.total_seats_blocked ?? 0),
        )
        setTotalSeatsBooked(
          String(row.summary?.total_seats_booked ?? row.total_seats_booked ?? 0),
        )
        setMultiplePnrs(
          pnrsToFormValue(row.summary?.multiple_pnrs ?? row.multiple_pnrs),
        )
        setBlockSource(
          row.summary?.booking_source ||
            row.booking_source ||
            row.block_source ||
            '',
        )
        setDistribution(distributionFromRow(row))
        setFreeCabinBags(bagsToFormValue(row.carry_on_baggage))
        setFreeCheckedBags(bagsToFormValue(row.free_baggage))
        setSeatReleaseDate(
          toDateInput(row.summary?.seat_release_date ?? row.seat_release_date),
        )
        setFareRulesEn(row.fare_rules_en || '')
        setFareRulesFr(row.fare_rules_fr || '')
        setIsActive(row.is_active === true)
        setSegments(itineraryToSegments(row))
        setPricing(faresToPaxPricing(row.fares))
        setCurrency(currencyFromFares(row.fares))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load block')
        const empty = emptyFormState()
        setTripType(empty.tripType)
        setPlatingCarrier(empty.platingCarrier)
        setFareType(empty.fareType)
        setTicketingDate(empty.ticketingDate)
        setTotalSeatsBlocked(empty.totalSeatsBlocked)
        setTotalSeatsBooked(empty.totalSeatsBooked)
        setMultiplePnrs(empty.multiplePnrs)
        setBlockSource(empty.blockSource)
        setDistribution(empty.distribution)
        setFreeCabinBags(empty.freeCabinBags)
        setFreeCheckedBags(empty.freeCheckedBags)
        setSeatReleaseDate(empty.seatReleaseDate)
        setFareRulesEn(empty.fareRulesEn)
        setFareRulesFr(empty.fareRulesFr)
        setSegments(empty.segments)
        setPricing(empty.pricing)
        setCurrency(empty.currency)
      } finally {
        setLoadingEdit(false)
      }
    }

    void load()
  }, [editingId])

  const updateSegment = (
    segmentKey: string,
    patch: Partial<SegmentDraft> | ((segment: SegmentDraft) => Partial<SegmentDraft>),
  ) => {
    setSegments((prev) =>
      prev.map((segment) =>
        segment.key !== segmentKey
          ? segment
          : {
              ...segment,
              ...(typeof patch === 'function' ? patch(segment) : patch),
            },
      ),
    )
  }

  const updateSegmentSearchField = (
    segmentKey: string,
    patch: Partial<Pick<SegmentDraft, 'origin' | 'destination' | 'departureDate'>>,
  ) => {
    setSegments((prev) => {
      const outboundKey = prev[0]?.key
      const updatingOutbound = segmentKey === outboundKey

      const next = prev.map((segment) => {
        if (segment.key !== segmentKey) return segment

        const updated = { ...segment, ...patch }
        const searchChanged =
          updated.origin !== segment.origin ||
          updated.destination !== segment.destination ||
          updated.departureDate !== segment.departureDate
        if (
          searchChanged &&
          (segment.scheduleOptions.length > 0 ||
            segment.selectedOptionIndex != null ||
            segment.flights.length > 0)
        ) {
          return {
            ...updated,
            scheduleOptions: [],
            selectedOptionIndex: null,
            flights: [],
            scheduleError: null,
          }
        }
        return updated
      })

      if (!updatingOutbound || next.length < 2) return next

      const outbound = next[0]
      const returnSeg = next[1]
      const syncedOrigin = outbound.destination
      const syncedDestination = outbound.origin
      const odChanged =
        returnSeg.origin !== syncedOrigin ||
        returnSeg.destination !== syncedDestination
      const returnDateInvalid =
        Boolean(returnSeg.departureDate) &&
        Boolean(outbound.departureDate) &&
        !isReturnDepartureAfterOutbound(
          outbound.departureDate,
          returnSeg.departureDate,
        )

      if (!odChanged && !returnDateInvalid) return next

      const shouldClearSchedules =
        odChanged ||
        returnDateInvalid ||
        returnSeg.scheduleOptions.length > 0 ||
        returnSeg.selectedOptionIndex != null ||
        returnSeg.flights.length > 0

      return [
        next[0],
        {
          ...returnSeg,
          origin: syncedOrigin,
          destination: syncedDestination,
          cabin: outbound.cabin,
          brandCode: outbound.brandCode,
          brandName: outbound.brandName,
          ...(returnDateInvalid ? { departureDate: '' } : {}),
          ...(shouldClearSchedules && (odChanged || returnDateInvalid)
            ? {
                scheduleOptions: [],
                selectedOptionIndex: null,
                flights: [],
                scheduleError: null,
              }
            : {}),
        },
        ...next.slice(2),
      ]
    })
  }

  const updateSegmentFareField = (
    segmentKey: string,
    patch: Partial<Pick<SegmentDraft, 'cabin' | 'brandCode' | 'brandName'>>,
  ) => {
    setSegments((prev) => {
      const outboundKey = prev[0]?.key
      const updatingOutbound = segmentKey === outboundKey

      const applyFare = (segment: SegmentDraft, farePatch: typeof patch) => {
        const updated = { ...segment, ...farePatch }
        const fareBasis = segmentFareBasis(updated)
        return {
          ...updated,
          flights: segment.flights.map((flight) => ({
            ...flight,
            fareBasis: { ...fareBasis },
          })),
        }
      }

      const next = prev.map((segment) =>
        segment.key === segmentKey ? applyFare(segment, patch) : segment,
      )

      if (!updatingOutbound || next.length < 2) return next

      const outbound = next[0]
      return [
        next[0],
        applyFare(next[1], {
          cabin: outbound.cabin,
          brandCode: outbound.brandCode,
          brandName: outbound.brandName,
        }),
        ...next.slice(2),
      ]
    })
  }

  const canSearchSchedules = (segment: SegmentDraft) => {
    if (
      !platingCarrier ||
      !segment.origin ||
      !segment.destination ||
      !segment.departureDate
    ) {
      return false
    }
    const outbound = segments[0]
    if (segment.key === segments[1]?.key && outbound) {
      return isReturnDepartureAfterOutbound(
        outbound.departureDate,
        segment.departureDate,
      )
    }
    return true
  }

  const handleFetchSchedules = async (
    segmentKey: string,
    options?: { replaceFlights?: boolean; openEditor?: boolean },
  ) => {
    const replaceFlights = options?.replaceFlights ?? true
    const openEditor = options?.openEditor ?? false
    const segment = segments.find((item) => item.key === segmentKey)
    if (!segment || !canSearchSchedules(segment)) return

    updateSegment(segmentKey, {
      loadingSchedules: true,
      scheduleError: null,
      scheduleOptions: [],
      selectedOptionIndex: null,
      ...(openEditor ? { editingSchedule: true } : {}),
      ...(replaceFlights ? { flights: [] } : {}),
      // Remember the search fields so a cancelled change can restore them.
      ...(!replaceFlights && segment.flights.length > 0
        ? {
            scheduleBackup: segment.scheduleBackup ?? {
              origin: segment.origin,
              destination: segment.destination,
              departureDate: segment.departureDate,
              cabin: segment.cabin,
            },
          }
        : {}),
    })

    try {
      const response = await fetchSabreFlightSchedules({
        origin: segment.origin,
        destination: segment.destination,
        validatingCarrier: platingCarrier,
        departureDate: segment.departureDate,
      })
      updateSegment(segmentKey, {
        scheduleOptions: response.data ?? [],
        loadingSchedules: false,
        scheduleError:
          (response.data ?? []).length === 0
            ? 'No schedules found for this search.'
            : null,
      })
    } catch (err) {
      updateSegment(segmentKey, {
        loadingSchedules: false,
        scheduleError:
          err instanceof Error ? err.message : 'Failed to fetch flight schedules',
        scheduleOptions: [],
      })
    }
  }

  const handleSelectSchedule = (segmentKey: string, optionIndex: number) => {
    updateSegment(segmentKey, (segment) => {
      const option = segment.scheduleOptions[optionIndex]
      if (!option) return segment
      return {
        selectedOptionIndex: optionIndex,
        flights: sabreOptionToFlights(option, segmentFareBasis(segment)),
        scheduleError: null,
        scheduleOptions: [],
        editingSchedule: false,
        scheduleBackup: null,
      }
    })
  }

  /** Applies inline edits to a segment's flights and keeps its search fields in step. */
  const handleSaveEditedFlights = (segmentKey: string, legs: FlightDraft[]) => {
    const first = legs[0]
    const last = legs[legs.length - 1]
    updateSegment(segmentKey, {
      flights: legs,
      ...(first && last
        ? {
            origin: first.origin,
            destination: last.destination,
            departureDate: first.departureDatetime.slice(0, 10),
            cabin: first.fareBasis.cabin,
          }
        : {}),
    })
  }

  /** Abandons a "Change schedule" search and keeps the flights already chosen. */
  const handleCancelScheduleChange = (segmentKey: string) => {
    updateSegment(segmentKey, (segment) => ({
      ...segment.scheduleBackup,
      scheduleOptions: [],
      selectedOptionIndex: null,
      scheduleError: null,
      loadingSchedules: false,
      editingSchedule: false,
      scheduleBackup: null,
    }))
  }

  const updatePaxPricing = (code: PaxCode, patch: Partial<PricingDraft>) => {
    setPricing((prev) => ({
      ...prev,
      [code]: { ...prev[code], ...patch },
    }))
  }

  const handleSubmit = async () => {
    if (!tripType || !canSubmit) return
    setSubmitting(true)
    setError(null)
    setSuccess(null)

    const payload: CreateInventoryPayload = {
      tripType,
      itinerary: {
        platingCarrier,
        validatingCarrier: platingCarrier,
        fareType,
        lastTicketableDate: ticketingDate,
        totalSeatsBlocked: seatCount(totalSeatsBlocked),
        totalSeatsBooked: seatCount(totalSeatsBooked),
        multiplePnrs: multiplePnrs.trim() || null,
        blockSource: blockSource.trim() || null,
        bookingSource: blockSource.trim() || null,
        ...distributionToPayload(distribution),
        carryOnBaggage: bagsToPayload(freeCabinBags),
        freeBaggage: bagsToPayload(freeCheckedBags),
        seatReleaseDate: seatReleaseDate || null,
        fareRulesEn: fareRulesEn.length > 0 ? fareRulesEn : null,
        fareRulesFr: fareRulesFr.length > 0 ? fareRulesFr : null,
      },
      summary: {
        lastTicketableDate: ticketingDate,
        seatReleaseDate: seatReleaseDate || null,
        totalSeatsBlocked: seatCount(totalSeatsBlocked),
        totalSeatsBooked: seatCount(totalSeatsBooked),
        bookingSource: blockSource.trim() || null,
        blockSource: blockSource.trim() || null,
        ...distributionToPayload(distribution),
        multiplePnrs: multiplePnrs.trim() || null,
      },
      pricing: PAX_TYPES.filter((pax) => isPaxFilled(pricing[pax.code])).map((pax) => ({
        currency,
        baseAmount: Number(pricing[pax.code].baseAmount),
        totalTaxes: Number(pricing[pax.code].totalTaxes),
        packageOnlyMarkup:
          pricing[pax.code].packageOnlyMarkup === ''
            ? null
            : Number(pricing[pax.code].packageOnlyMarkup),
        airOnlyMarkup:
          pricing[pax.code].airOnlyMarkup === ''
            ? null
            : Number(pricing[pax.code].airOnlyMarkup),
        passengerType: pax.type,
        passengerCode: pax.code,
      })),
      segments: segments.map((segment, segmentIndex) => ({
        segmentOrder: segmentIndex + 1,
        direction: segmentIndex === 0 ? 'outbound' : 'inbound',
        flights: segment.flights.map((flight, flightIndex) => ({
          flightNumber: flight.flightNumber,
          carrier: flight.carrier,
          operatingCarrier: flight.operatingCarrier || undefined,
          operatingCarrierName: flight.operatingCarrierName || undefined,
          departureAirport: flight.origin,
          departureDatetime: flight.departureDatetime,
          arrivalAirport: flight.destination,
          arrivalDatetime: flight.arrivalDatetime,
          aircraftType: flight.aircraftType || undefined,
          durationMin: Number(flight.durationMin),
          flightOrder: flightIndex + 1,
          fareBasis: {
            cabin: flight.fareBasis.cabin,
            classOfService: flight.fareBasis.classOfService || undefined,
            fareBasisCode: flight.fareBasis.fareBasisCode || undefined,
            brandCode: flight.fareBasis.brandCode || undefined,
            brandName: flight.fareBasis.brandName || undefined,
          },
        })),
      })),
    }

    try {
      const result = editingId
        ? await updateInventory(editingId, payload)
        : await createInventory(payload)
      setSuccess(
        editingId
          ? `Updated block #${result.data.id}`
          : `Created block #${result.data.id}`,
      )
      if (!editingId) {
        setTripType('')
        setPlatingCarrier('')
        setFareType(DEFAULT_FARE_TYPE)
        setTicketingDate('')
        setTotalSeatsBlocked('0')
        setTotalSeatsBooked('0')
        setMultiplePnrs('')
        setBlockSource('')
        setDistribution(EMPTY_DISTRIBUTION)
        setFreeCabinBags('')
        setFreeCheckedBags('')
        setSeatReleaseDate('')
        setFareRulesEn('')
        setFareRulesFr('')
        setSegments([])
        setPricing(EMPTY_PAX_PRICING)
        setCurrency(DEFAULT_CURRENCY)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : editingId ? 'Update failed' : 'Create failed')
    } finally {
      setSubmitting(false)
    }
  }

  const detailValues: BlockDetailsValues = {
    platingCarrier,
    blockSource,
    totalSeatsBlocked,
    totalSeatsBooked,
    ticketingDate,
    seatReleaseDate,
    multiplePnrs,
    distribution,
  }

  const updateDetail = <K extends keyof BlockDetailsValues>(
    key: K,
    value: BlockDetailsValues[K],
  ) => {
    const setters: {
      [P in keyof BlockDetailsValues]: (next: BlockDetailsValues[P]) => void
    } = {
      platingCarrier: setPlatingCarrier,
      blockSource: setBlockSource,
      totalSeatsBlocked: setTotalSeatsBlocked,
      totalSeatsBooked: setTotalSeatsBooked,
      ticketingDate: setTicketingDate,
      seatReleaseDate: setSeatReleaseDate,
      multiplePnrs: setMultiplePnrs,
      distribution: setDistribution,
    }
    ;(setters[key] as (next: BlockDetailsValues[K]) => void)(value)
  }

  return (
    <>
    <PageHeader
      eyebrow="Inventory"
      title={editingId ? `Edit block #${editingId}` : 'Create block'}
      description={
        editingId
          ? 'Update the block details, itinerary, and pricing for this block.'
          : 'Fill in the block details, then add the itinerary and pricing.'
      }
      actions={
        editingId ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              Status
            </Typography>
            <Switch
              checked={isActive}
              disabled={savingStatus}
              slotProps={{ input: { 'aria-label': 'Status' } }}
              onChange={(_event, next) => {
                const previous = isActive
                setIsActive(next)
                setSavingStatus(true)
                setError(null)
                updateBlockActive(editingId, next)
                  .then((result) => {
                    setIsActive(result.data.is_active)
                  })
                  .catch((err: unknown) => {
                    setIsActive(previous)
                    setError(err instanceof Error ? err.message : 'Status update failed')
                  })
                  .finally(() => setSavingStatus(false))
              }}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: '#fff',
                  '&.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'success.main',
                    opacity: 1,
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: 'error.main',
                  opacity: 1,
                },
              }}
            />
            {savingStatus ? <CircularProgress size={18} /> : null}
          </Stack>
        ) : undefined
      }
    />
    <Stack spacing={2.5}>

      {loadingEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {!loadingEdit && (
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>
            Block space
          </Typography>

          <BlockDetailsSection values={detailValues} onChange={updateDetail} />

          <Divider sx={{ my: 3 }} />

            <Stack spacing={3}>
              <Tabs
                value={activeTab}
                onChange={(_event, next) => setActiveTab(next)}
                aria-label="Block sections"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Itinerary" />
                <Tab label="Pricing" />
              </Tabs>

              {activeTab === 0 ? (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                      Trip type
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      color="primary"
                      value={tripType || null}
                      onChange={(_event, nextType: TripType | null) => {
                        if (!nextType) return
                        setTripType(nextType)
                        setSegments(buildSegments(nextType))
                        setActiveTab(0)
                        setSuccess(null)
                      }}
                      aria-label="Trip type"
                    >
                      <ToggleButton value="OW" sx={{ px: 2.5, fontWeight: 700 }}>
                        One way
                      </ToggleButton>
                      <ToggleButton value="RT" sx={{ px: 2.5, fontWeight: 700 }}>
                        Round trip
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {!tripType ? (
                    <Alert severity="info">
                      Choose One way or Round trip to build the itinerary.
                    </Alert>
                  ) : (
                    <>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                      Baggage allowance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Free cabin bags"
                          value={freeCabinBags}
                          onChange={(event) => setFreeCabinBags(event.target.value)}
                          helperText="Pieces per passenger"
                          slotProps={{ htmlInput: { min: 0, step: 1 } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Free checked bags"
                          value={freeCheckedBags}
                          onChange={(event) => setFreeCheckedBags(event.target.value)}
                          helperText="Pieces per passenger"
                          slotProps={{ htmlInput: { min: 0, step: 1 } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Stack spacing={2}>
                    {segments.map((segment, segmentIndex) => {
                      const isReturnSegment = segmentIndex === 1
                      const outboundDepartureDate = segments[0]?.departureDate || ''
                      const returnDateTooEarly =
                        isReturnSegment &&
                        Boolean(segment.departureDate) &&
                        Boolean(outboundDepartureDate) &&
                        !isReturnDepartureAfterOutbound(
                          outboundDepartureDate,
                          segment.departureDate,
                        )

                      const showSearchFields =
                        !editingId ||
                        segment.editingSchedule ||
                        segment.flights.length === 0

                      return (
                        <Paper
                          key={segment.key}
                          variant="outlined"
                          sx={{ p: 2.5 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            sx={{ alignItems: 'center', mb: 2 }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 2,
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '& svg': { fontSize: 18 },
                              }}
                            >
                              {isReturnSegment ? <FlightLandIcon /> : <FlightTakeoffIcon />}
                            </Box>
                            <Box>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {segment.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {segment.flights.length > 0
                                  ? `${segment.flights.length} flight${segment.flights.length === 1 ? '' : 's'} selected`
                                  : 'Search schedules and pick an option'}
                              </Typography>
                            </Box>
                          </Stack>

                          {showSearchFields ? (
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Origin"
                                  value={segment.origin}
                                  onChange={(event) =>
                                    updateSegmentSearchField(segment.key, {
                                      origin: event.target.value.toUpperCase(),
                                    })
                                  }
                                  placeholder="YYZ"
                                  slotProps={{ htmlInput: { maxLength: 3 } }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Destination"
                                  value={segment.destination}
                                  onChange={(event) =>
                                    updateSegmentSearchField(segment.key, {
                                      destination: event.target.value.toUpperCase(),
                                    })
                                  }
                                  placeholder="DEL"
                                  slotProps={{ htmlInput: { maxLength: 3 } }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <DateField
                                  required
                                  label="Departure date"
                                  value={segment.departureDate}
                                  error={returnDateTooEarly}
                                  helperText={
                                    returnDateTooEarly
                                      ? 'Must be after outbound departure date'
                                      : isReturnSegment && outboundDepartureDate
                                        ? 'Must be after the outbound departure'
                                        : undefined
                                  }
                                  onChange={(value) =>
                                    updateSegmentSearchField(segment.key, {
                                      departureDate: value,
                                    })
                                  }
                                  minDate={
                                    isReturnSegment && outboundDepartureDate
                                      ? nextCalendarDay(outboundDepartureDate)
                                      : undefined
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Autocomplete
                                  options={CABINS}
                                  value={segment.cabin || null}
                                  onChange={(_, value) =>
                                    updateSegmentFareField(segment.key, {
                                      cabin: value ?? '',
                                    })
                                  }
                                  renderInput={(params) => (
                                    <TextField {...params} label="Cabin" required />
                                  )}
                                />
                              </Grid>
                              {segment.flights.length === 0 ? (
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={
                                      segment.loadingSchedules ? (
                                        <CircularProgress
                                          size={16}
                                          color="inherit"
                                        />
                                      ) : (
                                        <ScheduleIcon />
                                      )
                                    }
                                    disabled={
                                      !canSearchSchedules(segment) ||
                                      segment.loadingSchedules
                                    }
                                    onClick={() =>
                                      void handleFetchSchedules(segment.key)
                                    }
                                    sx={{ height: 48 }}
                                  >
                                    Fetch flight schedules
                                  </Button>
                                </Grid>
                              ) : null}
                            </Grid>
                          ) : null}

                          {showSearchFields &&
                          !canSearchSchedules(segment) &&
                          segment.flights.length === 0 ? (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              Needs validating carrier, origin, destination, and
                              departure date.
                            </Alert>
                          ) : null}

                          {segment.flights.length > 0 ? (
                            <Box sx={{ mt: showSearchFields ? 2 : 0 }}>
                              <SelectedFlightsCard
                                legs={segment.flights}
                                loading={segment.loadingSchedules}
                                changeDisabled={
                                  !canSearchSchedules(segment) ||
                                  segment.loadingSchedules
                                }
                                onChangeSchedule={() =>
                                  void handleFetchSchedules(segment.key, {
                                    replaceFlights: false,
                                    openEditor: Boolean(editingId),
                                  })
                                }
                                onSaveLegs={(legs) =>
                                  handleSaveEditedFlights(segment.key, legs)
                                }
                              />
                            </Box>
                          ) : null}

                          {segment.loadingSchedules ? (
                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <CircularProgress size={16} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Searching schedules…
                                </Typography>
                              </Stack>
                              {[0, 1].map((index) => (
                                <Skeleton
                                  key={index}
                                  variant="rounded"
                                  height={index === 0 ? 96 : 150}
                                  sx={{ borderRadius: 3 }}
                                />
                              ))}
                            </Stack>
                          ) : null}

                          {segment.scheduleError ? (
                            <Alert
                              severity="warning"
                              sx={{ mt: 2 }}
                              action={
                                segment.flights.length > 0 ? (
                                  <Button
                                    size="small"
                                    color="inherit"
                                    onClick={() =>
                                      handleCancelScheduleChange(segment.key)
                                    }
                                  >
                                    Keep current flights
                                  </Button>
                                ) : undefined
                              }
                            >
                              {segment.scheduleError}
                            </Alert>
                          ) : null}

                          {segment.scheduleOptions.length > 0 ? (
                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {segment.flights.length > 0
                                    ? `Alternative schedules (${segment.scheduleOptions.length})`
                                    : `Schedule options (${segment.scheduleOptions.length})`}
                                </Typography>
                                {segment.flights.length > 0 ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CloseIcon />}
                                    onClick={() =>
                                      handleCancelScheduleChange(segment.key)
                                    }
                                  >
                                    Cancel change
                                  </Button>
                                ) : null}
                              </Stack>
                              {segment.scheduleOptions.map(
                                (option, optionIndex) => (
                                  <SabreScheduleOptionCard
                                    key={`${segment.key}-opt-${optionIndex}`}
                                    option={option}
                                    selected={
                                      segment.selectedOptionIndex === optionIndex
                                    }
                                    onSelect={() =>
                                      handleSelectSchedule(
                                        segment.key,
                                        optionIndex,
                                      )
                                    }
                                  />
                                ),
                              )}
                            </Stack>
                          ) : null}

                        </Paper>
                      )
                    })}
                  </Stack>
                    </>
                  )}
                </Stack>
              ) : null}

              {activeTab === 1 ? (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Pricing
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      At least one of Adult, Child, or Infant must be completed.
                      Empty passenger types are not saved.
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ alignItems: 'center', mb: 2.5 }}
                    >
                      <TextField
                        select
                        size="small"
                        label="Currency"
                        value={currency}
                        onChange={(event) => setCurrency(event.target.value)}
                        sx={{ width: 140 }}
                      >
                        {CURRENCIES.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="body2" color="text.secondary">
                        Applies to every passenger type below.
                      </Typography>
                    </Stack>
                    {!hasCompletePaxFare ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Fill at least one pricing row: Adult, Child, or Infant.
                      </Alert>
                    ) : null}
                    {hasPartialPaxFare ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        A started pricing row needs both base amount and tax, or
                        leave both blank.
                      </Alert>
                    ) : null}
                    <Stack spacing={2} divider={<Divider flexItem />}>
                      {PAX_TYPES.map((pax) => {
                        const Icon = pax.icon
                        const row = pricing[pax.code]
                        return (
                          <Stack
                            key={pax.code}
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ alignItems: { md: 'flex-start' } }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                alignItems: 'center',
                                minWidth: 100,
                                pt: { md: 0.75 },
                              }}
                            >
                              <Icon color="action" />
                              <Typography sx={{ fontWeight: 700 }}>
                                {pax.label}
                              </Typography>
                            </Stack>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1.5}
                              divider={
                                <Divider
                                  orientation="vertical"
                                  flexItem
                                  sx={{ display: { xs: 'none', md: 'block' } }}
                                />
                              }
                              sx={{ flex: 1, alignItems: { md: 'flex-start' } }}
                            >
                              <Grid container spacing={1.5} sx={{ flex: 1 }}>
                                <Grid size={{ xs: 4 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={`Base (${currency})`}
                                    value={row.baseAmount}
                                    onChange={(event) =>
                                      updatePaxPricing(pax.code, {
                                        baseAmount: event.target.value,
                                      })
                                    }
                                    slotProps={{
                                      htmlInput: { min: 0, step: '0.01' },
                                    }}
                                  />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={`Tax (${currency})`}
                                    value={row.totalTaxes}
                                    onChange={(event) =>
                                      updatePaxPricing(pax.code, {
                                        totalTaxes: event.target.value,
                                      })
                                    }
                                    slotProps={{
                                      htmlInput: { min: 0, step: '0.01' },
                                    }}
                                  />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Total"
                                    value={paxTotal(row)}
                                    slotProps={{
                                      htmlInput: {
                                        readOnly: true,
                                        tabIndex: -1,
                                      },
                                    }}
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        bgcolor: 'action.hover',
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Grid
                                container
                                spacing={1.5}
                                sx={{ flex: { md: '0 0 38%' } }}
                              >
                                <Grid size={{ xs: 6, md: 6 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Pkg markup"
                                    value={row.packageOnlyMarkup ?? ''}
                                    onChange={(event) =>
                                      updatePaxPricing(pax.code, {
                                        packageOnlyMarkup: event.target.value,
                                      })
                                    }
                                    autoComplete="off"
                                    slotProps={{
                                      htmlInput: {
                                        min: 0,
                                        step: '0.01',
                                        autoComplete: 'off',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid size={{ xs: 6, md: 6 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Air markup"
                                    value={row.airOnlyMarkup ?? ''}
                                    onChange={(event) =>
                                      updatePaxPricing(pax.code, {
                                        airOnlyMarkup: event.target.value,
                                      })
                                    }
                                    autoComplete="off"
                                    slotProps={{
                                      htmlInput: {
                                        min: 0,
                                        step: '0.01',
                                        autoComplete: 'off',
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Stack>
                          </Stack>
                        )
                      })}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1.5, fontWeight: 700 }}
                    >
                      Fare rules
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Fare rules (EN)"
                          value={fareRulesEn}
                          onChange={(event) => setFareRulesEn(event.target.value)}
                          multiline
                          minRows={6}
                          placeholder="English fare rules…"
                          helperText="Optional — tabs and line breaks are preserved"
                          slotProps={{
                            input: {
                              sx: {
                                fontFamily:
                                  'ui-monospace, SFMono-Regular, Menlo, monospace',
                                whiteSpace: 'pre-wrap',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Fare rules (FR)"
                          value={fareRulesFr}
                          onChange={(event) => setFareRulesFr(event.target.value)}
                          multiline
                          minRows={6}
                          placeholder="Règles tarifaires en français…"
                          helperText="Optional — tabs and line breaks are preserved"
                          slotProps={{
                            input: {
                              sx: {
                                fontFamily:
                                  'ui-monospace, SFMono-Regular, Menlo, monospace',
                                whiteSpace: 'pre-wrap',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              ) : null}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{
                  position: 'sticky',
                  bottom: 16,
                  zIndex: 2,
                  alignItems: { sm: 'center' },
                  justifyContent: 'space-between',
                  p: 1.5,
                  pl: { sm: 2.5 },
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 12px 32px -8px rgba(15, 23, 42, 0.18)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {canSubmit
                    ? 'All required fields are complete.'
                    : seatsOverbooked
                      ? 'Booked seats cannot exceed blocked seats.'
                      : 'Complete the required fields in every tab to save.'}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={!canSubmit || submitting}
                  onClick={() => void handleSubmit()}
                >
                  {editingId ? 'Update block' : 'Create block'}
                </Button>
              </Stack>
            </Stack>
        </Paper>
      )}
    </Stack>
    </>
  )
}
